import { FileCopy } from '@mui/icons-material'
import { Box, ButtonGroup } from '@mui/material'
import Button from '@mui/material/Button'
import { Field, FormikContext, getIn } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useCallback, useMemo } from 'react'

import formikConnect from '@src/lib/formikConnect'
import FormikErrorChecker from '@src/lib/FormikErrorChecker'

import { ShareSettingDialogFormValues } from './ShareSettingForm'

interface OrganicRetailerOverridesProps {
  formik: FormikContext<ShareSettingDialogFormValues>
}

const OrganicRetailerOverrides: React.FC<OrganicRetailerOverridesProps> = (
  props,
) => {
  const isCarousel = props.formik.values.model.child_attachments.length > 1
  const selectedIndex = props.formik.values.carouselIndex
  const setSelectedIndex = useCallback(
    (i: number) => {
      props.formik.setFieldValue('carouselIndex', i)
    },
    [props.formik],
  )

  const selector = useMemo(() => {
    const buttons = Array(props.formik.values.model.child_attachments.length)
      .fill(null)
      .map((_x, i) => (
        <FormikErrorChecker
          fields={[`model.child_attachments[${i}]`]}
          key={props.formik.values.model.child_attachments[i]?.id || undefined}
        >
          {(childAttachmentErrors) => {
            return (
              <Button
                color={childAttachmentErrors.areAllValid ? 'primary' : 'error'}
                variant={i === selectedIndex ? 'contained' : 'outlined'}
                onClick={() => setSelectedIndex(i)}
              >
                {i + 1}
              </Button>
            )
          }}
        </FormikErrorChecker>
      ))

    return <ButtonGroup>{buttons}</ButtonGroup>
  }, [
    props.formik.values.model.child_attachments,
    selectedIndex,
    setSelectedIndex,
  ])

  const allowUrlOverride = isCarousel
    ? getIn(
        props.formik.values,
        `model.child_attachments[${selectedIndex}].allow_retailer_url_override`,
      )
    : getIn(props.formik.values, `model.allow_retailer_url_override`)

  const urlFieldName = isCarousel
    ? `model.child_attachments[${selectedIndex}].link`
    : `model.link`

  const urlField = (
    <Field
      component={TextField}
      disabled={!allowUrlOverride}
      fullWidth
      helperText={
        !allowUrlOverride && 'Overrides have been disabled by the brand'
      }
      label="URL"
      name={urlFieldName}
    />
  )

  const handleApplyToAllClick = () => {
    const item = props.formik.values.model.child_attachments[selectedIndex]

    if (!item) {
      return
    }

    const newValue = props.formik.values.model.child_attachments.map((x) => ({
      ...x,
      link: item.link,
    }))

    props.formik.setFieldValue('model.child_attachments', newValue)
  }

  return (
    <div>
      {selector}
      {urlField}
      {isCarousel ? (
        <Box textAlign="right">
          <Button
            color="secondary"
            startIcon={<FileCopy />}
            variant="outlined"
            onClick={() => {
              handleApplyToAllClick()
            }}
          >
            {I18n.t('content.applyToAllCards')}
          </Button>
        </Box>
      ) : undefined}
    </div>
  )
}

export default formikConnect(OrganicRetailerOverrides)
